import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";

// fields: [{name,label,type:'text'|'textarea'|'number'|'image'|'list', placeholder}]
export default function CrudPanel({ title, endpoint, fields, empty, thumbField, primaryField, subField }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const path = ["courses", "results", "gallery", "testimonials", "videos"].includes(endpoint) ? `/${endpoint}` : `/${endpoint}`;
      const { data } = await api.get(path);
      setItems(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (item) => {
    const f = { ...empty, ...item };
    fields.forEach((fd) => {
      if (fd.type === "list" && Array.isArray(f[fd.name])) f[fd.name] = f[fd.name].join(", ");
      if (fd.type === "lines" && Array.isArray(f[fd.name])) f[fd.name] = f[fd.name].join("\n");
    });
    setEditing(item); setForm(f); setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((fd) => {
        if (fd.type === "list") payload[fd.name] = (payload[fd.name] || "").split(",").map((s) => s.trim()).filter(Boolean);
        if (fd.type === "lines") payload[fd.name] = (payload[fd.name] || "").split("\n").map((s) => s.trim()).filter(Boolean);
        if (fd.type === "number") payload[fd.name] = Number(payload[fd.name]) || 0;
      });
      if (editing) await api.put(`/admin/${endpoint}/${editing.id}`, payload);
      else await api.post(`/admin/${endpoint}`, payload);
      toast.success(editing ? "Updated" : "Added");
      setOpen(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/admin/${endpoint}/${item.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid={`panel-${endpoint}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
          <p className="text-sm text-slate-500">{items.length} item(s)</p>
        </div>
        <Button onClick={openNew} className="bg-blue-900 hover:bg-blue-950" data-testid={`${endpoint}-add-btn`}><Plus className="mr-1 h-4 w-4" /> Add New</Button>
      </div>

      {loading ? <Loader2 className="h-6 w-6 animate-spin text-blue-900" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, i) => (
            <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`${endpoint}-item-${i}`}>
              <div className="flex gap-3">
                {thumbField && (
                  <div className="h-16 w-16 rounded-lg bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                    {item[thumbField] ? (
                      endpoint === "videos"
                        ? <img src={`https://img.youtube.com/vi/${item[thumbField]}/default.jpg`} alt="" className="h-full w-full object-cover" />
                        : <img src={resolveImg(item[thumbField])} alt="" className="h-full w-full object-cover" />
                    ) : <span className="text-xs text-slate-400">No image</span>}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h4 className="font-bold text-slate-800 truncate">{item[primaryField] || "Untitled"}</h4>
                  {subField && <p className="text-xs text-slate-500 truncate">{item[subField]}</p>}
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(item)} data-testid={`${endpoint}-edit-${i}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(item)} data-testid={`${endpoint}-delete-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-slate-500 col-span-full">No items yet. Click "Add New" to create one.</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${endpoint}-dialog`}>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {fields.map((fd) => (
              <div key={fd.name}>
                {fd.type === "image" ? (
                  <ImageUpload label={fd.label} value={form[fd.name]} onChange={(v) => setForm((f) => ({ ...f, [fd.name]: v }))} />
                ) : (
                  <>
                    <Label className="mb-1.5 block text-sm font-semibold text-slate-700">{fd.label}</Label>
                    {fd.type === "select" ? (
                      <select value={form[fd.name] || ""} onChange={(e) => setForm((f) => ({ ...f, [fd.name]: e.target.value }))} data-testid={`${endpoint}-field-${fd.name}`}
                        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900/30">
                        {(fd.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : fd.type === "textarea" || fd.type === "lines" ? (
                      <Textarea value={form[fd.name] || ""} onChange={(e) => setForm((f) => ({ ...f, [fd.name]: e.target.value }))} placeholder={fd.placeholder} rows={3} data-testid={`${endpoint}-field-${fd.name}`} />
                    ) : (
                      <Input type={fd.type === "number" ? "number" : "text"} value={form[fd.name] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [fd.name]: e.target.value }))} placeholder={fd.placeholder} data-testid={`${endpoint}-field-${fd.name}`} />
                    )}
                    {fd.hint && <p className="text-xs text-slate-400 mt-1">{fd.hint}</p>}
                  </>
                )}
              </div>
            ))}
            <Button onClick={save} disabled={saving} className="w-full bg-blue-900 hover:bg-blue-950" data-testid={`${endpoint}-save-btn`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
